/* ─────────────────────────────────────────────
   LANGUAGE / URL HELPERS
   URL is the single source of truth for language:
     sr -> /<ruta>        (no prefix, default)
     en -> /en/<ruta>
     de -> /de/<ruta>
   These helpers are used by App.js (router basename),
   the Seo component (canonical / hreflang) and the
   language switcher links.
   ───────────────────────────────────────────── */

export const LANGS = ['sr', 'en', 'de'];
export const DEFAULT_LANG = 'sr';

/* ─────────────────────────────────────────────
   LOCALISED SLUGS

   Most routes use the same path in every language and only differ by the
   /en, /de prefix. A few are translated in the URL itself, because the slug
   is the keyword ("napredni kurs šminkanja" / "advanced makeup course").

   Key = the LOGICAL route, which is always identical to the Serbian slug.
   Everything else in the codebase (routeMeta, SEO_ROUTES, sitemap) keys off
   that logical route; only the URL that reaches the browser is translated.

   Adding a translated route: add it here AND to `sr` in routeMeta.mjs using
   the same logical key, then register every slug in routes/index.js.
   ───────────────────────────────────────────── */
export const ROUTE_SLUGS = {
  '/ponude': {
    sr: '/ponude',
    en: '/offers',
    de: '/angebote',
  },
  '/napredni-kurs-sminkanja': {
    sr: '/napredni-kurs-sminkanja',
    en: '/advanced-makeup-course',
    de: '/fortgeschrittenen-kurs',
  },
  '/farbanje-kose': {
    sr: '/farbanje-kose',
    en: '/hair-colouring',
    de: '/haare-faerben',
  },
  '/tretmani-kose': {
    sr: '/tretmani-kose',
    en: '/hair-treatments',
    de: '/haarbehandlungen',
  },
  '/musko-sisanje': {
    sr: '/musko-sisanje',
    en: '/mens-haircut',
    de: '/herrenhaarschnitt',
  },
  '/ekstenzije-za-kosu': {
    sr: '/ekstenzije-za-kosu',
    en: '/clip-in-hair-extensions',
    de: '/clip-in-extensions',
  },
  '/perike': {
    sr: '/perike',
    en: '/wigs',
    de: '/peruecken',
  },
};

/** Localised, basename-relative slug for a logical route. */
export function slugFor(route, lang) {
  const map = ROUTE_SLUGS[route];
  return (map && map[lang]) || route;
}

/**
 * Inverse of slugFor(): turn a basename-relative pathname back into the
 * logical route. Accepts any language's slug so a stale/cross-language URL
 * still resolves instead of 404-ing.
 */
export function routeForSlug(pathname) {
  const p = pathname || '/';
  const slug = p.length > 1 ? p.replace(/\/+$/, '') : '/';
  for (const [route, map] of Object.entries(ROUTE_SLUGS)) {
    if (Object.values(map).includes(slug)) return route;
  }
  return slug;
}

/** Every slug a logical route answers on - used to register <Route> paths. */
export function allSlugsFor(route) {
  const map = ROUTE_SLUGS[route];
  return map ? [...new Set(Object.values(map))] : [route];
}

export const SITE = 'https://www.visionbeauty.net';

/** <html lang> value per language */
export const HTML_LANG = { sr: 'sr-Latn-RS', en: 'en', de: 'de' };
/** hreflang value per language */
export const HREFLANG = { sr: 'sr-RS', en: 'en', de: 'de' };
/** Open Graph locale per language */
export const OG_LOCALE = { sr: 'sr_RS', en: 'en_US', de: 'de_DE' };

/** Detect the active language from a *full* pathname (incl. prefix). */
export function getLangFromPath(pathname) {
  const seg = (pathname || '/').split('/')[1];
  if (seg === 'en') return 'en';
  if (seg === 'de') return 'de';
  return 'sr';
}

/** Router basename for a language ('' for sr, '/en', '/de'). */
export function basenameFor(lang) {
  return lang === 'sr' ? '' : `/${lang}`;
}

/**
 * Strip a leading /en or /de prefix from a full pathname and return the
 * logical route, always starting with '/', never with a trailing slash
 * (except the root '/').
 */
export function stripLangPrefix(pathname) {
  const parts = (pathname || '/').split('/');
  let rest = pathname || '/';
  if (parts[1] === 'en' || parts[1] === 'de') {
    rest = '/' + parts.slice(2).join('/');
  }
  if (rest.length > 1) rest = rest.replace(/\/+$/, '');
  return routeForSlug(rest === '' ? '/' : rest);
}

/**
 * Build a full absolute URL (canonical / hreflang / og:url) for a logical
 * route in a given language. Never has a trailing slash except the root.
 */
export function urlFor(route, lang) {
  const base = `${SITE}${basenameFor(lang)}`;
  // Home (per language) keeps a trailing slash; every other route omits it.
  if (route === '/') return `${base}/`;
  return `${base}${slugFor(route, lang).replace(/\/+$/, '')}`;
}

/**
 * Build the in-site href for switching the current route to another language.
 * Used by the language switcher (<a href>), so Googlebot can crawl every
 * language version.
 *
 * The argument may be either a logical route or a localised slug (the Navbar
 * hands over location.pathname), so it goes through routeForSlug() first and
 * comes back out as the target language's own slug - /de/educations therefore
 * links to /de/fortgeschrittenen-kurs, never to the Serbian URL.
 */
export function switchHref(route, targetLang) {
  const slug = slugFor(routeForSlug(route), targetLang);
  const logical = slug === '/' ? '' : slug.replace(/\/+$/, '');
  const path = `${basenameFor(targetLang)}${logical}`;
  return path === '' ? '/' : path;
}

/**
 * In-site href for a logical route in the CURRENT language, without the
 * basename (React Router adds that). Use for <Link to={...}> whenever the
 * target may have a translated slug.
 */
export function linkTo(route, lang) {
  return slugFor(route, lang);
}
